import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Newspaper,
  Calendar,
  Edit2,
  Trash2,
  Star,
  Eye,
} from "lucide-react";
import { colors, customShadows } from "../../../../theme";
import { CommonChip } from "../../ui";

/**
 * Strip HTML tags to produce a clean preview snippet
 */
const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

/**
 * NewsCard Component
 * Displays a news article item in a responsive grid card format.
 */
const NewsCard = ({
  item,
  onEdit,
  onDelete,
  onToggleFeature,
  onPreview,
}) => {
  const {
    id,
    title,
    description,
    imageUrl,
    featured = false,
    updatedOn,
    createdOn,
  } = item || {};

  const snippet = stripHtml(description);
  const displayDate = updatedOn || createdOn;

  const formatDate = (dateVal) => {
    if (!dateVal) return "Recently";
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return "Recently";
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "Recently";
    }
  };

  return (
    <Card
      sx={{
        borderRadius: "16px",
        boxShadow: customShadows.cardFlat || "0 2px 12px rgba(0,0,0,0.03)",
        border: `1px solid ${colors.dashboard.cardBorder}`,
        bgcolor: colors.background.paper,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        height: "100%",
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: customShadows.cardHover || "0 12px 28px rgba(0,0,0,0.07)",
          borderColor: colors.slate[300],
        },
      }}
    >
      {/* Cover Image Container */}
      <Box
        sx={{
          width: "100%",
          height: 180,
          bgcolor: colors.slate[100],
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={title}
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = "none";
            }}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.3s ease",
              "&:hover": {
                transform: "scale(1.04)",
              },
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              color: colors.slate[400],
            }}
          >
            <Newspaper size={36} strokeWidth={1.5} />
            <Typography variant="caption" sx={{ color: colors.slate[400], fontWeight: 500 }}>
              AutoBET News
            </Typography>
          </Box>
        )}

        {/* Top Badges / Overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            right: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pointerEvents: "none",
          }}
        >
          {featured ? (
            <CommonChip
              label="Featured"
              startIcon={<Star size={12} fill="#eab308" color="#eab308" />}
              size="small"
              sx={{
                bgcolor: "rgba(254, 240, 138, 0.95)",
                color: "#854d0e",
                fontWeight: 700,
                fontSize: "0.72rem",
                backdropFilter: "blur(4px)",
                border: "1px solid rgba(250, 204, 21, 0.5)",
                pointerEvents: "auto",
              }}
            />
          ) : (
            <CommonChip
              label="Standard"
              size="small"
              sx={{
                bgcolor: "rgba(241, 245, 249, 0.9)",
                color: colors.slate[600],
                fontWeight: 600,
                fontSize: "0.72rem",
                backdropFilter: "blur(4px)",
                pointerEvents: "auto",
              }}
            />
          )}

          <Tooltip title={featured ? "Remove from Featured" : "Mark as Featured"}>
            <IconButton
              size="small"
              onClick={() => onToggleFeature && onToggleFeature(id, featured)}
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(4px)",
                color: featured ? "#eab308" : colors.slate[400],
                pointerEvents: "auto",
                "&:hover": {
                  bgcolor: "#ffffff",
                  color: "#eab308",
                  transform: "scale(1.1)",
                },
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              }}
            >
              <Star size={16} fill={featured ? "#eab308" : "none"} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Card Body */}
      <Box
        sx={{
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: "1.05rem",
            color: colors.slate[900],
            lineHeight: 1.35,
            mb: 1,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            cursor: onPreview ? "pointer" : "default",
            "&:hover": onPreview
              ? { color: colors.primary.main }
              : undefined,
          }}
          onClick={() => onPreview && onPreview(item)}
        >
          {title || "Untitled Article"}
        </Typography>

        {/* Snippet */}
        <Typography
          variant="body2"
          sx={{
            color: colors.slate[500],
            fontSize: "0.85rem",
            lineHeight: 1.5,
            mb: 2,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            flexGrow: 1,
          }}
        >
          {snippet || "No description provided."}
        </Typography>

        <Divider sx={{ my: 1.5, borderColor: colors.dashboard.tableBorder }} />

        {/* Footer Meta & Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: "auto",
          }}
        >
          {/* Date */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Calendar size={14} color={colors.slate[400]} />
            <Typography
              variant="caption"
              sx={{ color: colors.slate[400], fontWeight: 500 }}
            >
              {formatDate(displayDate)}
            </Typography>
          </Box>

          {/* Action Icons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Preview Article">
              <IconButton
                size="small"
                onClick={() => onPreview && onPreview(item)}
                sx={{
                  color: colors.slate[500],
                  "&:hover": {
                    color: colors.primary.main,
                    bgcolor: colors.primary.lighter,
                  },
                }}
              >
                <Eye size={16} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Edit Article">
              <IconButton
                size="small"
                onClick={() => onEdit && onEdit(item)}
                sx={{
                  color: colors.slate[500],
                  "&:hover": {
                    color: colors.primary.main,
                    bgcolor: colors.primary.lighter,
                  },
                }}
              >
                <Edit2 size={16} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete Article">
              <IconButton
                size="small"
                onClick={() => onDelete && onDelete(id)}
                sx={{
                  color: colors.slate[500],
                  "&:hover": {
                    color: colors.error.main || "#ef4444",
                    bgcolor: colors.error.lighter || "#fee2e2",
                  },
                }}
              >
                <Trash2 size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

NewsCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    imageUrl: PropTypes.string,
    featured: PropTypes.bool,
    updatedOn: PropTypes.string,
    createdOn: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onToggleFeature: PropTypes.func,
  onPreview: PropTypes.func,
};

export default NewsCard;
